import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ThreeDViewer from '../ThreeDViewer';

function ThreeDViewerPage() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    
    // Force one full page load when 3D page is shown so heap is fresh (avoids SPA state / WebGL leak)
    const needsReload = searchParams.get('_3d') !== '1';
    const returnUrl = searchParams.get('return_url');

    useEffect(() => {
        if (needsReload) {
            const params = new URLSearchParams(searchParams);
            params.set('_3d', '1');
            const qs = params.toString();
            window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
        }
    }, [needsReload, searchParams]);

    // On production (static hosting), browser Back from another route can restore this page from bfcache.
    // One3D/WebGL doesn't reliably survive bfcache restores → can render as a blank white page.
    // Force a reload on bfcache restoration so the viewer re-initializes cleanly.
    useEffect(() => {
        const onPageShow = (e) => {
            if (e && e.persisted) {
                window.location.reload();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    // When user clicks browser Back: intercept so we do a full reload to returnUrl (clears 3D heap). Must run unconditionally (rules of hooks).
    useEffect(() => {
        if (needsReload) return;
        const goBackWithFullReload = () => {
            const target = returnUrl ? decodeURIComponent(returnUrl) : (document.referrer || '/');
            window.location.href = target.startsWith('http') ? target : window.location.origin + target;
        };
        const handlePopState = () => { goBackWithFullReload(); };
        window.history.pushState({ from3dBack: true }, '', window.location.href);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [needsReload, returnUrl]);

    if (needsReload) return null;

    const modelId = searchParams.get('model_id');
    const variantId = searchParams.get('variant_id');
    const yearId = searchParams.get('year_id');
    const entrySource = searchParams.get('entry_source') || undefined;

    const handleClose = () => {
        const target = returnUrl ? decodeURIComponent(returnUrl) : (document.referrer || '/');
        window.location.href = target.startsWith('http') ? target : window.location.origin + target;
    };

    return (
        <div style={{ height: '100vh', width: '100vw', overflow: 'hidden' }}>
            <ThreeDViewer 
                accessoryId={parseInt(id)} 
                onClose={handleClose}
                initialModelId={modelId ? parseInt(modelId, 10) : null}
                initialVariantId={variantId ? parseInt(variantId, 10) : null}
                initialYearId={yearId ? parseInt(yearId, 10) : null}
                entrySource={entrySource}
            />
        </div>
    );
}

export default ThreeDViewerPage;
